import type { TourContent, TourFaqEntry } from '../types/tour.types'

/**
 * Parses a knowledge-base `.md` file into a TourContent object. The format is a
 * small, fixed convention (front-matter + `## Section` headings) so no external
 * markdown/YAML library is needed:
 *
 *   ---
 *   key: dashboard
 *   title: Dashboard
 *   ---
 *   ## Value Proposition   → valueProp (raw markdown)
 *   ## Benefits            → benefits (- list)
 *   ## Tutorial            → tutorial (raw markdown)
 *   ## Best Practices      → bestPractices (- list)
 *   ## Videos              → videos (- [title](url))
 *   ## FAQ                 → faq (### question / answer)
 *   ## AI Questions        → aiQuestions (- list)
 *   ## AI Actions          → aiActions (- [label](prompt))
 */
export function parseTourContent(raw: string, fallbackKey: string): TourContent {
  const { frontMatter, body } = splitFrontMatter(raw)
  const sections = splitSections(body)

  const linkListOf = (name: string) => parseLinkList(sections[name] ?? '')

  return {
    key: frontMatter.key || fallbackKey,
    title: frontMatter.title || fallbackKey,
    valueProp: (sections['value proposition'] ?? '').trim(),
    benefits: parseBulletList(sections['benefits'] ?? ''),
    tutorial: (sections['tutorial'] ?? '').trim(),
    bestPractices: parseBulletList(sections['best practices'] ?? ''),
    videos: linkListOf('videos'),
    faq: parseFaq(sections['faq'] ?? ''),
    aiQuestions: parseBulletList(sections['ai questions'] ?? ''),
    aiActions: linkListOf('ai actions').map((v) => ({ label: v.title, prompt: v.url })),
  }
}

function splitFrontMatter(raw: string): { frontMatter: Record<string, string>; body: string } {
  const match = raw.match(/^\s*---\s*\n([\s\S]*?)\n---\s*\n?/)
  if (!match) return { frontMatter: {}, body: raw }
  const frontMatter: Record<string, string> = {}
  for (const line of match[1].split('\n')) {
    const idx = line.indexOf(':')
    if (idx === -1) continue
    const k = line.slice(0, idx).trim().toLowerCase()
    const v = line.slice(idx + 1).trim()
    if (k) frontMatter[k] = v
  }
  return { frontMatter, body: raw.slice(match[0].length) }
}

function splitSections(body: string): Record<string, string> {
  const sections: Record<string, string> = {}
  const parts = body.split(/^##\s+/m)
  for (const part of parts) {
    const nl = part.indexOf('\n')
    if (nl === -1) continue
    const heading = part.slice(0, nl).trim().toLowerCase()
    if (!heading) continue
    sections[heading] = part.slice(nl + 1).trim()
  }
  return sections
}

function parseBulletList(section: string): string[] {
  return section
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.startsWith('- '))
    .map((l) => l.slice(2).trim())
    .filter(Boolean)
}

function parseLinkList(section: string): { title: string; url: string }[] {
  const out: { title: string; url: string }[] = []
  for (const line of section.split('\n')) {
    const m = line.trim().match(/^- \[([^\]]+)\]\(([^)]+)\)/)
    if (m) out.push({ title: m[1].trim(), url: m[2].trim() })
  }
  return out
}

function parseFaq(section: string): TourFaqEntry[] {
  const out: TourFaqEntry[] = []
  const blocks = section.split(/^###\s+/m).slice(1)
  for (const block of blocks) {
    const nl = block.indexOf('\n')
    const q = (nl === -1 ? block : block.slice(0, nl)).trim()
    const a = (nl === -1 ? '' : block.slice(nl + 1)).trim()
    if (q) out.push({ q, a })
  }
  return out
}
